import type { BufferGeometry, InstancedMesh, Object3D } from "three";
import { Emitter } from "@core/events/emitter";
import type { FrameModel } from "@core/frame/frame";
import type { ThreeEngine } from "@runtime/engine/engineService";
import {
  getCampfireBatchStatistics,
  isCampfireBatchOwned,
  isCampfireBatchSource,
} from "@runtime/assets/entityMeshBatcher";

/**
 * `SceneStatisticsService` — the measurement half of the scene-statistics
 * panel (`components/panels/system/SceneStatisticsView.vue`).
 *
 * Unity reference: `ViewModels/SceneStatisticsViewModel.cs` +
 * `Content/Resources/Views/SceneStatisticsView.prefab`. Unity does this work
 * inside the ViewModel; the web cannot, because `src/models/**` and the
 * ViewModel layer may not import `three` (`Campfire_Web_Runtime.md`), and the
 * counts come from `WebGLRenderer.info` and a scene-graph walk. Hence the split:
 * this runtime service measures, `useSceneStatistics` binds, the view renders.
 *
 * The engine arrives through `attachEngine()` at viewport-mount time — the same
 * inversion `IPreviewCaptureHost` uses — so the service can be registered in the
 * IOC bootstrap before a renderer exists and reports "no engine" until one does.
 * It must never resolve `ENGINE_SERVICE` itself: `EngineService.app` throws
 * before `initialize()`, and `Ioc.isRegistered` is true from bootstrap onward,
 * so it is not a usable readiness signal.
 *
 * Collection is opt-in (`setEnabled`) and refcounted: with the panel closed the
 * service holds no `FrameModel` subscription and costs exactly nothing —
 * matching Unity's `if (!IsVisible) return;` at the top of its `Update()`.
 */

/**
 * Sampling window, and the recount cadence for the scene walk. This is the web
 * spelling of Unity's `k_fpsBufferSize = 30` (30 frames ≈ half a second at 60
 * fps). Unity re-walks the scene every 5 s; the web can afford 500 ms because
 * the walk is synchronous, where Unity's awaits a frame per entity.
 */
const K_SAMPLE_WINDOW_MS = 500;

/**
 * Frames longer than this are discarded from the average instead of averaged
 * in. A delta this large is not a slow frame — it is `requestAnimationFrame`
 * resuming after the tab was hidden or throttled, and folding one in reports
 * things like "0 FPS / 27955 ms" for the whole next window.
 */
const K_MAX_FRAME_SECONDS = 1;

/**
 * The scene-entity render root (`sceneEntityRenderer.ensureRoot`). Resolved by
 * name on every sample rather than cached — `disposeRoot()` drops the Group on
 * project unload and `ensureRoot()` builds a fresh one, so a held reference goes
 * stale on every project switch.
 */
const K_CONTENT_ROOT_NAME = "campfire-scenes";

export interface SceneStatistics {
  /** Frames per second over the last sample window. `null` until the first window closes. */
  readonly fps: number | null;
  /** Mean milliseconds per frame over the last sample window. */
  readonly frameMs: number | null;
  /**
   * Frames per second that actually DREW over the last window. Equal to `fps`
   * unless render on demand is on (`EngineService`), when a still viewport
   * ticks every frame but draws a handful.
   */
  readonly drawsPerSecond: number | null;
  /**
   * Why those frames drew, busiest first — `EngineService`'s draw reasons
   * (a continuous source's label, "request", "heartbeat", …) as rates over the
   * last window. What keeps a still viewport drawing, at a glance.
   */
  readonly drawReasons: ReadonlyArray<{ readonly reason: string; readonly perSecond: number }>;
  /** Draw calls issued across every pass of the last frame that drew. */
  readonly drawCalls: number;
  /** Triangles in the active scene's entities (Unity `TriangleCount`). */
  readonly triangles: number;
  /** Vertices in the same set (Unity `VertexCount`). */
  readonly vertices: number;
  /** Mesh instances contributing them (Unity `MeshInstanceCount`). */
  readonly meshInstances: number;
  /** Distinct geometries behind those instances (Unity `MeshCount`). */
  readonly meshes: number;
  /** Live GPU-side textures (`renderer.info.memory.textures`). */
  readonly textures: number;
  /**
   * Used JS heap in bytes, or `null` where the browser does not expose it.
   * Only Chromium ships the non-standard `performance.memory`; Safari and
   * Firefox report `null` and the panel says so rather than inventing a number.
   */
  readonly jsHeapUsedBytes: number | null;
  /** JS heap ceiling in bytes, same availability caveat as `jsHeapUsedBytes`. */
  readonly jsHeapLimitBytes: number | null;
  /** False until an engine is attached — renderer-sourced fields are 0 then. */
  readonly hasEngine: boolean;
}

const EMPTY_STATS: SceneStatistics = {
  fps: null,
  frameMs: null,
  drawsPerSecond: null,
  drawReasons: [],
  drawCalls: 0,
  triangles: 0,
  vertices: 0,
  meshInstances: 0,
  meshes: 0,
  textures: 0,
  jsHeapUsedBytes: null,
  jsHeapLimitBytes: null,
  hasEngine: false,
};

/** Chromium-only, non-standard. Absent in Safari/Firefox. */
interface PerformanceMemory {
  readonly usedJSHeapSize: number;
  readonly totalJSHeapSize: number;
  readonly jsHeapSizeLimit: number;
}

interface GeometryTotals {
  triangles: number;
  vertices: number;
  meshInstances: number;
  meshes: number;
}

export class SceneStatisticsService {
  readonly onStatisticsChanged = new Emitter<SceneStatistics>();

  private readonly m_frame: FrameModel;
  private m_engine: ThreeEngine | null = null;
  private readonly m_offFns: (() => void)[] = [];
  private m_consumers = 0;

  private m_windowFrames = 0;
  /** Frames in the window that drew — see `SceneStatistics.drawsPerSecond`. */
  private m_windowDraws = 0;
  private m_windowSeconds = 0;
  /** Held across frames that do not draw, which would otherwise read as 0 calls. */
  private m_lastDrawCalls = 0;
  /** `onLateTick` carries `deltaSeconds: 0`, so the real delta rides over from `onTick`. */
  private m_pendingDelta = 0;
  private m_statistics: SceneStatistics = EMPTY_STATS;
  /** Reused across samples so the walk does not allocate a fresh Set twice a second. */
  private readonly m_uniqueGeometries = new Set<BufferGeometry>();

  constructor(frame: FrameModel) {
    this.m_frame = frame;
  }

  get statistics(): SceneStatistics {
    return this.m_statistics;
  }

  /**
   * Registered by `ViewportView` on mount and cleared (`null`) on unmount, so a
   * disposed renderer is never sampled after the canvas goes away.
   */
  attachEngine(engine: ThreeEngine | null): void {
    if (this.m_engine && this.m_engine !== engine) this.releaseInfoOwnership(this.m_engine);
    this.m_engine = engine;
    if (!engine) {
      this.publish({ ...EMPTY_STATS, hasEngine: false });
      return;
    }
    // Flip `hasEngine` as soon as the renderer exists rather than waiting for
    // the first sample, or the panel flashes "Waiting for the viewport…" for a
    // whole window every time it opens over a perfectly healthy viewport.
    // Guarded so re-attaching the same engine cannot wipe a live reading.
    if (!this.m_statistics.hasEngine) {
      this.publish({ ...this.m_statistics, hasEngine: true });
    }
    if (this.isCollecting) this.takeInfoOwnership(engine);
  }

  private get isCollecting(): boolean {
    return this.m_offFns.length > 0;
  }

  /**
   * Refcounted so two open panels (or a re-mount overlapping an unmount) cannot
   * leave the tick subscription dangling or tear it down early.
   */
  setEnabled(enabled: boolean): void {
    this.m_consumers = Math.max(0, this.m_consumers + (enabled ? 1 : -1));
    if (this.m_consumers > 0) this.subscribe();
    else this.unsubscribe();
  }

  private subscribe(): void {
    if (this.isCollecting) return;
    this.m_windowFrames = 0;
    this.m_windowDraws = 0;
    this.m_windowSeconds = 0;
    this.m_lastDrawCalls = 0;
    this.m_pendingDelta = 0;
    // `onTick` runs before the render and `onLateTick` after it (see
    // `EngineService.renderFrame`), so the counters are zeroed and then read
    // around exactly one frame's worth of draw work.
    this.m_offFns.push(
      this.m_frame.onTick.on(({ deltaSeconds }) => {
        this.m_pendingDelta = deltaSeconds;
        this.m_engine?.renderer.info.reset();
      }),
    );
    this.m_offFns.push(this.m_frame.onLateTick.on(() => this.sample(this.m_pendingDelta)));
    if (this.m_engine) this.takeInfoOwnership(this.m_engine);
  }

  private unsubscribe(): void {
    if (!this.isCollecting) return;
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    if (this.m_engine) this.releaseInfoOwnership(this.m_engine);
  }

  /**
   * `renderer.info` resets itself at the top of every `render()` call, so under a
   * multi-pass pipeline the counters only ever describe the LAST pass —
   * `SelectionOutlineController` installs an `EffectComposer`
   * (`engineService.setRenderPipeline`) whose final pass is a full-screen quad,
   * which is why draw calls otherwise read as `1`. Owning the reset lets them
   * accumulate every pass of one frame, which is the honest per-frame cost:
   * selecting something genuinely does add an outline pass.
   *
   * Scoped to the panel being open, and nothing else in `src/` reads
   * `renderer.info`, so the flag is safe to borrow and hand back.
   */
  private takeInfoOwnership(engine: ThreeEngine): void {
    engine.renderer.info.autoReset = false;
  }

  private releaseInfoOwnership(engine: ThreeEngine): void {
    engine.renderer.info.autoReset = true;
  }

  dispose(): void {
    this.unsubscribe();
    this.m_consumers = 0;
    this.m_engine = null;
    this.m_uniqueGeometries.clear();
  }

  /**
   * Frame-counting over a fixed window rather than Unity's mean of
   * `1 / Time.deltaTime`: averaging reciprocals over-weights the fast frames,
   * whereas frames-per-elapsed-second is the actual rate. The window also
   * throttles the scene walk.
   */
  private sample(deltaSeconds: number): void {
    // Drop, do not average: see `K_MAX_FRAME_SECONDS`. Discarding the frame
    // also leaves the window un-advanced, so the stall cannot stretch it.
    if (deltaSeconds > K_MAX_FRAME_SECONDS) return;
    this.m_windowFrames++;
    this.m_windowSeconds += deltaSeconds;
    // The counters were zeroed on this frame's tick, so any call means it drew.
    const calls = this.m_engine?.renderer.info.render.calls ?? 0;
    if (calls > 0) {
      this.m_windowDraws++;
      this.m_lastDrawCalls = calls;
    }
    if (this.m_windowSeconds * 1000 < K_SAMPLE_WINDOW_MS) return;

    const seconds = this.m_windowSeconds;
    const frames = this.m_windowFrames;
    const draws = this.m_windowDraws;
    this.m_windowFrames = 0;
    this.m_windowDraws = 0;
    this.m_windowSeconds = 0;

    const fps = seconds > 0 ? frames / seconds : null;
    const frameMs = frames > 0 ? (seconds * 1000) / frames : null;
    const drawsPerSecond = seconds > 0 ? draws / seconds : null;
    const drawReasons = summarizeDrawReasons(this.m_engine?.takeDrawReasons() ?? null, seconds);
    const memory = readJsHeap();
    const engine = this.m_engine;

    if (!engine) {
      this.publish({
        ...EMPTY_STATS,
        fps,
        frameMs,
        drawsPerSecond,
        drawReasons,
        jsHeapUsedBytes: memory?.usedJSHeapSize ?? null,
        jsHeapLimitBytes: memory?.jsHeapSizeLimit ?? null,
      });
      return;
    }

    const content = engine.root.getObjectByName(K_CONTENT_ROOT_NAME) ?? null;
    const geometry = this.countVisibleGeometry(content);
    const info = engine.renderer.info;
    this.publish({
      fps,
      frameMs,
      drawsPerSecond,
      drawReasons,
      drawCalls: this.m_lastDrawCalls,
      triangles: geometry.triangles,
      vertices: geometry.vertices,
      meshInstances: geometry.meshInstances,
      meshes: geometry.meshes,
      textures: info.memory.textures,
      jsHeapUsedBytes: memory?.usedJSHeapSize ?? null,
      jsHeapLimitBytes: memory?.jsHeapSizeLimit ?? null,
      hasEngine: true,
    });
  }

  /**
   * Walk the scene-entity subtree summing geometry, pruning invisible subtrees.
   *
   * Scoped to `campfire-scenes` — NOT the whole `scene` — to match Unity's
   * `UpdateSceneStats`, which walks `GetEntities(activeScene)` and nothing else.
   * Counting the whole graph would fold in the skydome, floor grid, table,
   * console, remote-user avatars, Spark's head, and every tool gizmo, none of
   * which is the project content a modeller is trying to budget.
   *
   * The `visible` prune is load-bearing, not hygiene: `sceneEntityRenderer`
   * keeps EVERY scene visited this session parented under that root and only
   * toggles `root.visible` when the active scene changes. Without the prune the
   * panel reports the union of every scene the user has opened. It is also what
   * excludes user-hidden entities, matching Unity's `GetComponentsInChildren`
   * defaulting to active-only.
   *
   * Deliberately NOT sourced from `renderer.info.render.triangles`: under the
   * `EffectComposer` pipeline that counter describes composited passes and
   * full-screen quads, and it would jump the moment something is selected.
   */
  private countVisibleGeometry(root: Object3D | null): GeometryTotals {
    const totals: GeometryTotals = { triangles: 0, vertices: 0, meshInstances: 0, meshes: 0 };
    if (!root) return totals;

    // Unity's `MeshCount` is a `HashSet<Mesh>` — distinct meshes, where
    // `MeshInstanceCount` counts placements. `GltfAssetCache` clones share
    // geometry by reference, so the same Set semantics carry over.
    const unique = this.m_uniqueGeometries;
    unique.clear();

    const walk = (node: Object3D): void => {
      if (!node.visible) return;
      const batchEntries = getCampfireBatchStatistics(node);
      if (batchEntries) {
        for (const entry of batchEntries) {
          if (!entry.visible) continue;
          totals.triangles += triangleCount(entry.geometry);
          totals.vertices += entry.geometry.getAttribute("position")?.count ?? 0;
          totals.meshInstances++;
          unique.add(entry.geometry);
        }
      }
      // `isMesh` rather than "has a geometry": `Line` / `LineSegments` / `Points`
      // carry a position attribute too, and dividing their vertex count by 3
      // would invent triangles that are never rasterized.
      const mesh = node as Partial<InstancedMesh> & { geometry?: BufferGeometry };
      if (
        !batchEntries
        && !isCampfireBatchSource(node)
        && !isCampfireBatchOwned(node)
        && mesh.isMesh
        && mesh.geometry
      ) {
        const geometry = mesh.geometry;
        // An `InstancedMesh` draws its geometry `count` times.
        const instances = mesh.isInstancedMesh ? (node as InstancedMesh).count : 1;
        totals.triangles += triangleCount(geometry) * instances;
        totals.vertices += (geometry.getAttribute("position")?.count ?? 0) * instances;
        totals.meshInstances += instances;
        unique.add(geometry);
      }
      for (const child of node.children) walk(child);
    };

    walk(root);
    totals.meshes = unique.size;
    unique.clear();
    return totals;
  }

  private publish(statistics: SceneStatistics): void {
    this.m_statistics = statistics;
    this.onStatisticsChanged.emit(statistics);
  }
}

/**
 * Triangles a mesh geometry would draw. Honours `drawRange` when it narrows the
 * buffer (`count` defaults to `Infinity`, so the `min` is a no-op otherwise).
 */
function triangleCount(geometry: BufferGeometry): number {
  const position = geometry.getAttribute("position");
  if (!position) return 0;
  const available = geometry.index ? geometry.index.count : position.count;
  const drawn = Math.min(available, geometry.drawRange.count);
  return Math.floor(drawn / 3);
}

function readJsHeap(): PerformanceMemory | null {
  const memory = (performance as Performance & { memory?: PerformanceMemory }).memory;
  return memory && typeof memory.usedJSHeapSize === "number" ? memory : null;
}

/** The three busiest draw reasons of a window, as rates. */
function summarizeDrawReasons(
  counts: ReadonlyMap<string, number> | null,
  seconds: number,
): SceneStatistics["drawReasons"] {
  if (!counts || seconds <= 0) return [];
  return [...counts]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([reason, count]) => ({ reason, perSecond: count / seconds }));
}

export const SCENE_STATISTICS_SERVICE = Symbol.for("SceneStatisticsService");
